// Thin wrapper around rrweb's record-only entry point. Owns the monotonic
// seq counter and batches emitted rrweb events into chunks on a timer/count
// threshold. Never calls /finalize, /close, or /abort -- that orchestration
// lives in index.ts, driven by channel.ts and consent.ts.

import { record } from 'rrweb';
import type { eventWithTime } from '@rrweb/types';
import { FLUSH_INTERVAL_MS, FLUSH_MAX_EVENTS } from './config';
import type { SelectorConfig } from './masking';

/** rrweb's ignoreClass only supports a class name (no ignoreSelector), so
 * customer-provided `data-ui-ignore` selectors are translated into this
 * fixed class, applied at start and kept live via a MutationObserver so
 * elements added later by the customer's SPA are still covered. */
const IGNORE_CLASS = 'uir-ignore';

export interface RecorderDeps {
  selectors: SelectorConfig;
  /** Seq of the last chunk already emitted for this recording (from a resumed
   * session). The first chunk this recorder emits is `initialSeq + 1`, so the
   * sequence stays monotonic across a page navigation. Defaults to 0. */
  initialSeq?: number;
  onChunk: (seq: number, events: eventWithTime[]) => void;
  doc?: Document;
}

export interface RecorderHandle {
  /** Flush the current rrweb tail into a numbered chunk without stopping.
   * Used immediately before pagehide/visibility drains so the last few
   * seconds before a navigation are durable on the next page. */
  flush: () => void;
  stop: () => void;
  getFinalSeq: () => number;
}

function applyIgnoreClass(selectors: string[], doc: Document): () => void {
  if (selectors.length === 0) return () => {};
  const apply = () => {
    for (const sel of selectors) {
      try {
        doc.querySelectorAll(sel).forEach((el) => el.classList.add(IGNORE_CLASS));
      } catch {
        // invalid/unsupported selector from customer config -- skip it, don't throw.
      }
    }
  };
  apply();
  const observer = new MutationObserver(apply);
  observer.observe(doc.documentElement, { childList: true, subtree: true });
  return () => observer.disconnect();
}

export function startRecording(deps: RecorderDeps): RecorderHandle {
  const doc = deps.doc ?? document;
  let seq = deps.initialSeq ?? 0;
  let buffer: eventWithTime[] = [];
  // stop() is now invoked from two teardown paths (finalizeAndStop's reorder,
  // then stopAndClear) -- guard so the second call is a safe no-op instead of
  // double-flushing a chunk or double-calling rrweb's own stop.
  let stopped = false;

  const stopIgnoreObserver = applyIgnoreClass(deps.selectors.ignore, doc);

  function flush(): void {
    if (buffer.length === 0) return;
    seq += 1;
    const events = buffer;
    buffer = [];
    deps.onChunk(seq, events);
  }

  const flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);

  const stopRecordingFn = record({
    emit: (event) => {
      buffer.push(event as eventWithTime);
      if (buffer.length >= FLUSH_MAX_EVENTS) flush();
    },
    maskAllInputs: true,
    maskTextClass: 'rr-mask',
    maskTextSelector: deps.selectors.mask.length > 0 ? deps.selectors.mask.join(',') : undefined,
    blockClass: 'rr-block',
    blockSelector: deps.selectors.block.length > 0 ? deps.selectors.block.join(',') : undefined,
    ignoreClass: IGNORE_CLASS,
    slimDOMOptions: { script: true },
  });

  return {
    flush,
    stop() {
      if (stopped) return;
      stopped = true;
      clearInterval(flushTimer);
      flush();
      stopRecordingFn?.();
      stopIgnoreObserver();
    },
    getFinalSeq() {
      return seq;
    },
  };
}
